<template>
  <div v-if="event">
    <digital-event-preloader type="container" :active="useFullPagePreloader" />

    <div class="room-list-container container-fluid">
      <div class="row">
        <div v-if="rooms.length > 1 && showRooms && multipleRooms.length && !isMobile" class="room-list">
          <digital-event-room-list-card
            v-for="room in rooms"
            :key="'room-' + room.id"
            :room="room"
          ></digital-event-room-list-card>
        </div>

        <digital-event-room-list-select
          v-if="rooms.length > 1 && showRooms && isMobile"
          :rooms="rooms"
        ></digital-event-room-list-select>

        <div v-if="rooms.length && activeRoom && roomAssignments.length" class="col-sm-12 parent">
          <router-view>
            <digital-event-room></digital-event-room>
          </router-view>
        </div>
      </div>

      <div v-if="showDashboard">
        <digital-event-room-admin-dashboard></digital-event-room-admin-dashboard>
      </div>

      <transition name="fade">
        <digital-event-room-celebration-template
          v-show="isCelebration"
          :message="celebrationMessage"
          @close-banner="closeCelebration()"
        >
        </digital-event-room-celebration-template>
      </transition>
    </div>
  </div>
</template>

<script>
import AdminDashboard from "@/components/DigitalEvent/RoomList/Room/AdminDashboard";
import CelebrationsBanner from "@/components/DigitalEvent/RoomList/Room/CelebrationBanner";
import Room from "./Room/Room";
import RoomListCard from "./Room/components/RoomListCard.vue";
import RoomListSelect from "./Room/components/RoomListSelect.vue";
import BaseMixin from "../mixins/BaseMixin";
import CheckBrowserMixin from "../mixins/checkBrowserMixin";
import PermissionsMixin from "../mixins/Permissions/PermissionsMixin";
import AnnouncementsMixin from "../mixins/AnnouncementsMixin";
import WindowWithMixin from "@/components/DigitalEvent/RoomList/Room/mixins/WindowWithMixin";
import { EVESSIO_DOMAINS, ANNOUNCEMENT_TYPES, ROOMS_NAVIGATION_STYLE } from "@/helpers/constants";
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "RoomList",
  mixins: [BaseMixin, CheckBrowserMixin, AnnouncementsMixin, PermissionsMixin, WindowWithMixin],
  components: {
    "digital-event-room-admin-dashboard": AdminDashboard,
    "digital-event-room": Room,
    "digital-event-room-list-card": RoomListCard,
    "digital-event-room-celebration-template": CelebrationsBanner,
    "digital-event-room-list-select": RoomListSelect
  },
  data() {
    return {
      userTable: null,
      isCelebration: false,
      celebrationMessage: {},
      timerId: null
    };
  },
  computed: {
    ...mapState({
      firstLogin: state => state.users.firstLogin,
      event: state => state.event.context,
      activeRoom: state => state.room.activeRoom
    }),
    ...mapGetters({
      rooms: "room/rooms",
      multipleRooms: "room/multipleRooms",
      currentUser: "users/currentUser",
      defaultRoom: "room/defaultRoom",
      useFullPagePreloader: "useFullPagePreloader"
    }),
    /** Check user can join past event */
    canJoinPastEvent() {
      return this.canJoinEventByTime || this.isAdmin;
    },
    user() {
      return this.$store.state.users.user;
    },
    roomAssignments() {
      return this.$store.state.room.roomAssignments;
    },
    /**
     * Checking that dashboard may be shown
     */
    showDashboard() {
      if (!this.rooms.length || !this.event.ENABLE_ADMIN_DASHBOARD || !this.isAdmin) return false;

      return (
        EVESSIO_DOMAINS.includes(this.$store.state.users.domain) ||
        (Array.isArray(this.event.ADMIN_DOMAINS) && this.event.ADMIN_DOMAINS.includes(this.$store.state.users.domain))
      );
    },
    showRooms() {
      return this.event.ROOMS_NAVIGATION_STYLE === ROOMS_NAVIGATION_STYLE.BUTTONS;
    },
    getActiveRoom() {
      if (this.currentUser.roomUUID) {
        return this.rooms.find(room => room.uuid === this.currentUser.roomUUID).SLUG;
      } else if (this.defaultRoom) {
        return this.defaultRoom.SLUG;
      } else {
        return this.rooms[0].SLUG;
      }
    },
    isMobile() {
      return this.windowWidth < 768;
    }
  },
  methods: {
    ...mapActions({
      getRooms: "room/getRooms"
    }),
    showCelebration(message) {
      this.celebrationMessage = message;
      this.isCelebration = true;

      if (message.timeout) {
        let timeout = message.timeout * 1000;

        this.timerId = setTimeout(() => {
          this.closeCelebration();
          this.timerId = null;
        }, timeout);
      }
    },
    closeCelebration() {
      this.isCelebration = false;
    }
  },
  watch: {
    announce(newValue) {
      if (newValue) {
        switch (newValue.announceType) {
          case ANNOUNCEMENT_TYPES.CELEBRATION:
            if (this.timerId) clearTimeout(this.timerId);
            this.showCelebration(newValue);
            break;

          default:
            this.showAnnounce(newValue);
        }
      }
    }
  },
  async created() {
    if (this.isNotAllowableBrowser) {
      this.$router.push("/auth/login");
      return;
    } else {
      this.$store.commit("setPreloaderFlag", true);
      await this.getRooms(this.event.uuid);
      await this.$store.dispatch("loadInitData", {
        roomSlug: this.$route.params.roomSlug || null
      });

      if (!this.hasAccess) {
        this.$store.commit("restrictAccess");
        this.goTo(this.ROUTES.DIGITAL_EVENT_AUTH_TABLE_INFO);
        this.$store.commit("setPreloaderFlag", false);
        return;
      }

      if (this.firstLogin) {
        this.goTo(this.ROUTES.DIGITAL_EVENT_AUTH_TABLE_INFO);
        return;
      }

      if (!this.canJoinEvent) {
        this.$router.push("/auth/login");
        return;
      }

      if (!this.$store.getters["tables/userTable"]) await this.$store.dispatch("tables/getOrCreateUserTable");

      await this.$store.dispatch("users/initUser");
      await this.$store.dispatch("room/getRoomState");
      this.$store.commit("room/setActiveRoom", this.getActiveRoom);

      this.goTo(this.ROUTES.DIGITAL_EVENT_ROOM, {
        roomSlug: this.$store.getters["room/activeRoom"].SLUG
      });

      this.$store.commit("setPreloaderFlag", false);

      this.$store.dispatch("config/clearEventStartTimer");
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
